import type {NormalizedCacheObject} from '@apollo/client';
import {
    type ApolloClientProxy,
    type BaseHttpCodes,
    EntityBaseGqlService,
    type GetDownloadLinkRequestPayload,
    type IDownloadByLinkService,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {Mutation, Query, QueryGetCountryLevelReportArgs, Subscription} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {CountryLevelPartnerReportRequestBuilder} from './requestBuilder';
import type {
    CountryLevelPartnerReportDownloadFields,
    CountryLevelPartnerReportQueryFilterKeys,
    CountryLevelPartnerReportQueryKeys,
    CountryLevelReport,
} from './types';

@injectable()
export class CountryLevelPartnerReportService
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetCountryLevelReportArgs,
        CountryLevelPartnerReportQueryKeys,
        Mutation,
        Subscription,
        BaseHttpCodes,
        CountryLevelPartnerReportQueryFilterKeys
    >
    implements IDownloadByLinkService<CountryLevelPartnerReportDownloadFields>
{
    protected readonly _requestBuilder: CountryLevelPartnerReportRequestBuilder;
    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const builder = new CountryLevelPartnerReportRequestBuilder();
        super(client, builder, messageLocalizer);
        this._requestBuilder = builder;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, CountryLevelPartnerReportQueryKeys>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, CountryLevelReport>> {
        return this.fetchData(requestPayload).pipe(
            map(r => {
                const summaryItem: CountryLevelReport = r.responsePayload.getCountryLevelReport?.summary
                    ? {
                          is_summary: true,
                          date_ts: {seconds: 0},
                          ...r.responsePayload.getCountryLevelReport.summary,
                          __typename: 'CountryLevelReport',
                      }
                    : null;
                const items = summaryItem
                    ? [...(r.responsePayload?.getCountryLevelReport?.items ?? []), summaryItem]
                    : (r.responsePayload?.getCountryLevelReport?.items ?? []);
                return {
                    ...r,
                    requestPayload,
                    responsePayload: {
                        items: items ?? [],
                        total: r.responsePayload.getCountryLevelReport?.total_count ?? 0,
                    },
                };
            })
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<CountryLevelPartnerReportDownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<CountryLevelPartnerReportDownloadFields>, string>> {
        const request = this._requestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportCountryLevelReport?.url,
            }))
        );
    }

    protected getErrorSeverity(_code: BaseHttpCodes): number {
        return 0;
    }
}
